import React from 'react';
import { routePaths } from 'components/Routes/data';
import { Redirect, Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { PendingApproval } from './PendingApproval';
import { Completed } from './Completed';
import {
  TabItem,
  Box,
  Heading,
  TabList,
  Tabs,
  FilterChipGroup,
  FilterChipDatePicker,
  Dropdown,
  DropdownOverlay,
  FilterChipSelectInput,
  ActionList,
  ActionListItem,
  DatesRangeValue,
  ListViewFilters,
} from '@razorpay/blade/components';
import { useFilters } from 'hooks/useFilters';
import { filtersMap } from './data';
import { datePresets, completedStatusFilters, completedStatusMap } from './constant';
import { useQuery } from 'react-query';
import { getFlexiReimbursementClaimsList } from 'api/gql-utils/flexibleBenefits/queries/getFlexiReiumbursementClaimsList';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { useSelector } from 'react-redux';
import { usePagination } from 'hooks/usePagination';
import useReduxSelector from 'utils/useReduxSelector';

export const FlexiReimbursements = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const history = useHistory();
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId);

  const isPendingTab = pathname.includes('pending-approvals');
  const isCompletedTab = pathname.includes('completed');

  const { currentFilters, applyFilter, removeFilter } = useFilters({
    filtersMap,
  });


  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: 10,
  });

  const startDate = currentFilters.startDate
    ? new Date(parseInt(currentFilters.startDate, 10))
    : null;
  const endDate = currentFilters.endDate ? new Date(parseInt(currentFilters.endDate, 10)) : null;

  const statusFilter = currentFilters.statusFilter || '';
  const statusFilterConfig = statusFilter ? completedStatusMap[statusFilter] : undefined;

  const handleDateRangeChange = (value: DatesRangeValue) => {
    if (value[0]) {
      applyFilter(filtersMap.startDate, value[0].getTime().toString());
    } else {
      removeFilter(filtersMap.startDate);
    }
    if (value[1]) {
      applyFilter(filtersMap.endDate, value[1].getTime().toString());
    } else {
      removeFilter(filtersMap.endDate);
    }
  };

  const handleClearDateRange = () => {
    removeFilter(filtersMap.startDate);
    removeFilter(filtersMap.endDate);
  };

  const handleStatusFilterChange = (values: string[]) => {
    if (values.length === 0) {
      removeFilter(filtersMap.statusFilter);
    } else {
      applyFilter(filtersMap.statusFilter, values[0]);
    }
  };

  const handleClearAllFilters = () => {
    handleClearDateRange();
    removeFilter(filtersMap.statusFilter);
  };

  const getStatusFilterValue = () => {
    if (isPendingTab) {
      return ['SUBMITTED'];
    }
    if (statusFilterConfig?.status) {
      return statusFilterConfig.status;
    }
    return ['APPROVED', 'PARTIALLY_APPROVED', 'REJECTED'];
  };

  const tenant_id = `org_${useReduxSelector(
    (state) => state?.loggedInUser?.data?.currentOrganization?.id || '',
  )}`;

  const {
    data: { claims, totalCount } = { claims: [], totalCount: 0 },
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['flexi-reimbursement-claims-list', currentPage, itemsPerPage, startDate, endDate, statusFilter, isPendingTab, loggedInUserId],
    queryFn: () =>
      getFlexiReimbursementClaimsList({
        offset: currentPage,
        limit: itemsPerPage,
        status: getStatusFilterValue(),
        ...(startDate && { start_time: Math.floor(startDate.getTime() / 1000).toString() }),
        ...(endDate && { end_time: Math.floor(endDate.getTime() / 1000).toString() }),
        ...(statusFilterConfig?.includeUserId && loggedInUserId
          ? { approver_id: loggedInUserId.toString() }
          : { tenant_id, merchant_id: tenant_id }),
      }),
    select: (data) => ({
      claims: data?.payrollListFlexiReimbursementClaims?.claims || [],
      totalCount: data?.payrollListFlexiReimbursementClaims?.total_count || 0,
    }),
  });

  if (pathname === routePaths.approvalWorkflows.myRequests.flexiReimbursement.root) {
    return (
      <Redirect to={routePaths.approvalWorkflows.myRequests.flexiReimbursement.pendingApprovals} />
    );
  }

  return (
    <Box padding={['spacing.5', 'spacing.8', 'spacing.8', 'spacing.8']}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="spacing.3">
        <Heading size="large" as="h1" color="surface.text.gray.normal">
          Flexi Reimbursement Requests
        </Heading>
      </Box>
      <Box>
        <Tabs
          variant="bordered"
          size="medium"
          orientation="horizontal"
          value={history.location.pathname}
          onChange={(value) => {
            removeFilter(filtersMap.statusFilter);
            history.push(value);
          }}>
          <TabList>
            <TabItem
              value={routePaths.approvalWorkflows.myRequests.flexiReimbursement.pendingApprovals}>
              Pending Approvals
            </TabItem>
            <TabItem value={routePaths.approvalWorkflows.myRequests.flexiReimbursement.completed}>
              Completed
            </TabItem>
          </TabList>
        </Tabs>
      </Box>
      <Box marginTop={'-48px'}>
        <ListViewFilters
          quickFilters={<Box />}
          selectedFiltersCount={(startDate ? 1 : 0) + (statusFilter ? 1 : 0)}>
          <Box paddingTop="spacing.5">
            <FilterChipGroup onClearButtonClick={handleClearAllFilters}>
              {isCompletedTab && (
                <Dropdown selectionType="single">
                  <FilterChipSelectInput
                    label="Status"
                    value={statusFilter}
                    onChange={({ values }) => handleStatusFilterChange(values)}
                    onClearButtonClick={() => removeFilter(filtersMap.statusFilter)}
                    accessibilityLabel="status filter"
                  />
                  <DropdownOverlay>
                    <ActionList>
                      {Object.entries(completedStatusFilters).map(([key, label]) => (
                        <ActionListItem key={key} title={label} value={key} />
                      ))}
                    </ActionList>
                  </DropdownOverlay>
                </Dropdown>
              )}
              <FilterChipDatePicker
                label="Date Range"
                selectionType="range"
                onApply={handleDateRangeChange}
                onClearButtonClick={handleClearDateRange}
                presets={datePresets}
                defaultValue={[startDate, endDate]}
              />
            </FilterChipGroup>
          </Box>
        </ListViewFilters>
      </Box>
      <Switch>
        <Route
          exact
          path={routePaths.approvalWorkflows.myRequests.flexiReimbursement.pendingApprovals}>
          <PendingApproval
            claimRequests={claims || []}
            isLoading={isLoading}
            isFetching={isFetching}
            refetch={refetch}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            setCurrentPage={setCurrentPage}
            setItemsPerPage={setItemsPerPage}
            totalCount={totalCount}
          />
        </Route>
        <Route exact path={routePaths.approvalWorkflows.myRequests.flexiReimbursement.completed}>
          <Completed
            claimRequests={claims || []}
            isLoading={isLoading}
            isFetching={isFetching}
            refetch={refetch}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            setCurrentPage={setCurrentPage}
            setItemsPerPage={setItemsPerPage}
            statusFilter={statusFilter}
            totalCount={totalCount}
          />
        </Route>
      </Switch>
    </Box>
  );
};
